// 지출 세부내역(CostMatrix)의 "AWS 인프라" 축을 만드는 순수 함수 — 2026-07-28.
//
// 왜 별도 파일인가: 이 축은 자동(BillingON 실청구)과 수기 입력(category="server")이 섞이고,
// 둘의 우선순위 판정을 "일자별로 먼저" 해야 맞는다(주/월로 접은 뒤 판정하면 수기 입력이 있는
// 하루가 그 주 전체의 자동값을 덮어버린다 — TrendSection/timeseries.awsExpenseKrw 가 같은
// 함정을 이미 겪었다). 컴포넌트 렌더 로직에 섞어두면 이 규칙을 단위 테스트할 수 없어 분리했다.
//
// 규칙(timeseries.js awsExpenseKrw 와 동일):
//   자동(BillingON)이 기본. 그 **날짜**에 수기 server 입력이 있으면 그 날짜는 수기가 이긴다.
//   이유: BillingON 은 1~2일 지연으로 당일/전일이 $0 또는 부분값이고, 월 청구서 확정 후
//   크레딧·할인·세금 차이를 사람이 정정해야 하는 경우가 있다.

/**
 * @param {Object} params
 * @param {Object<string, Object<string, number>>} params.byServiceDaily
 *   BillingON `/analytics/billingon-costs` 의 byServiceDaily — {서비스:{YYYY-MM-DD:usd}}.
 * @param {Array<{date:string, service?:string, amountUsd:number}>} params.serverExpenses
 *   수기 지출 중 category="server" 항목(통화는 호출측에서 USD 로 환산해 넘긴다).
 * @param {(date:string)=>string} params.bucketKeyFn 일→버킷 키(day/week/month) 변환.
 * @returns {{cells: Map<string, Map<string, number>>, manualDates: Set<string>,
 *            autoUsd: number, manualUsd: number, overriddenAutoUsd: number}}
 *   cells = 서비스(행 라벨) → 버킷 → USD.
 */
export function buildAwsAxis({ byServiceDaily = {}, serverExpenses = [], bucketKeyFn = (d) => d } = {}) {
  // 1) 수기 입력이 있는 날짜 집합 — 금액이 0 이하인 행은 "입력 없음"으로 본다.
  const manualDates = new Set();
  for (const e of serverExpenses) {
    if (!e?.date) continue;
    if ((e.amountUsd || 0) > 0) manualDates.add(e.date);
  }

  const cells = new Map();
  const add = (label, bucket, usd) => {
    if (!cells.has(label)) cells.set(label, new Map());
    const byBucket = cells.get(label);
    byBucket.set(bucket, (byBucket.get(bucket) || 0) + usd);
  };

  // 2) 자동(BillingON) — 수기가 이긴 날짜는 건너뛴다. 건너뛴 금액은 화면 안내용으로 합산.
  let autoUsd = 0;
  let overriddenAutoUsd = 0;
  for (const [service, byDate] of Object.entries(byServiceDaily || {})) {
    for (const [date, usd] of Object.entries(byDate || {})) {
      const v = usd || 0;
      if (!v) continue;
      if (manualDates.has(date)) {
        overriddenAutoUsd += v;
        continue;
      }
      autoUsd += v;
      add(service, bucketKeyFn(date), v);
    }
  }

  // 3) 수기 — 서비스명이 비어 있으면 "기타 서버비"로 모은다(기존 매트릭스 동작 유지).
  let manualUsd = 0;
  for (const e of serverExpenses) {
    const v = e?.amountUsd || 0;
    if (!v || !e?.date) continue;
    manualUsd += v;
    add(e.service || "기타 서버비", bucketKeyFn(e.date), v);
  }

  return { cells, manualDates, autoUsd, manualUsd, overriddenAutoUsd };
}

// Bedrock 실청구액 — BillingON 서비스명은 모델별로 쪼개져 오고("Claude Opus 4.8 (Amazon
// Bedrock Edition)", "Amazon Bedrock" 등) 표기가 계속 바뀌므로 이름에 Bedrock 이 들어간
// 서비스를 전부 합산한다. 원장 토큰×단가 추정과 대조하는 데만 쓴다(합계에는 안 쓴다).
export function bedrockActualUsd(byServiceDaily = {}) {
  let sum = 0;
  for (const [service, byDate] of Object.entries(byServiceDaily || {})) {
    if (!/bedrock/i.test(service)) continue;
    for (const usd of Object.values(byDate || {})) sum += usd || 0;
  }
  return sum;
}
